import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';

import { EntityVisibilitySelect } from '~/components/Shared/EntityVisibilitySelect/EntityVisibilitySelect';
import { validateShareField } from '~/components/Shared/EntityVisibilitySelect/utils';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { User } from '~/eds/src/components/User/User';
import { Form } from '~/eds/src/modules/Form/Form';
import { StateUpdater } from '~/eds/src/modules/Form/types';
import { requiredValidator } from '~/eds/src/modules/Form/validators';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import chatbot from '~/redux/slices/chatbot';
import { Nullable } from '~/types/Nullable';
import { QuestionGroup } from '~/types/QuestionGroup';
import { Uuid } from '~/types/Uuid';

import { SideContentTabs } from '../useChatBotSideContent';
import { QuestionSelect } from './QuestionSelect';

interface EditQuestionFormProps {
  questionGroup: Nullable<QuestionGroup>;
  onClose?: () => void;
}

const getFields = (
  isShareQuestionGroupEnabled: boolean,
  groupId?: Uuid,
  intl?: IntlShape,
) => ({
  name: {
    order: 1,
    key: 'name',
    type: 'field' as const,
    name: 'name',
    label:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.nameLabel',
        defaultMessage: 'Name',
        description: 'Label for question group name field',
      }) ?? '',
    input: TextInput,
    placeholder:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.namePlaceholder',
        defaultMessage: 'e.g. Contract Law Compliance',
        description: 'Placeholder for question group name field',
      }) ?? '',
    required: true,
  },
  description: {
    order: 2,
    key: 'description',
    type: 'field' as const,
    name: 'description',
    label:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.descriptionLabel',
        defaultMessage: 'Description',
        description: 'Label for question group description field',
      }) ?? 'Description',
    optional: true,
    input: TextArea,
  },
  ...(isShareQuestionGroupEnabled
    ? {
        shareField: {
          order: 3,
          key: 'shareField',
          type: 'field' as const,
          name: 'shareField',
          label:
            intl?.formatMessage({
              id: 'UseQuestionGroupFormModal.visibilityLabel',
              defaultMessage: 'Visibility',
              description: 'Label for question group visibility field',
            }) ?? 'Visibility',
          description:
            intl?.formatMessage({
              id: 'UseQuestionGroupFormModal.visibilityDescription',
              defaultMessage:
                'If visibility is set to anyone, changes to the question group will impact all users and groups.',
              description: 'Description for question group visibility field',
            }) ??
            'If visibility is set to anyone, changes to the question group will impact all users and groups.',
          input: EntityVisibilitySelect,
          inputProps: {
            enablePortal: false,
          },
        },
      }
    : {}),
  questions: {
    order: 4,
    key: 'questions',
    type: 'field' as const,
    name: 'questions',
    label:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.questionsLabel',
        defaultMessage: 'Questions',
        description: 'Label for questions field',
      }) ?? 'Questions',
    description:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.questionsDescription',
        defaultMessage:
          'Search and select the question(s) to add to the question group. You can add a maximum number of 25 items in the question group',
        description: 'Description for questions field',
      }) ??
      'Search and select the question(s) to add to the question group. You can add a maximum number of 25 items in the question group',
    input: QuestionSelect,
    inputProps: {
      groupId,
    },
    placeholder:
      intl?.formatMessage({
        id: 'UseQuestionGroupFormModal.questionsPlaceholder',
        defaultMessage: 'Search and select',
        description: 'Placeholder for questions field',
      }) ?? 'Search and select',
    required: true,
  },
});

type QuestionGroupDraft = Omit<
  QuestionGroup,
  'id' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt' | 'pinnedAt'
>;

type QuestionGroupForm = {
  name: string;
  description: string;
  questions: string[];
  shareField: {
    visibility: 'OPEN' | 'PRIVATE' | 'SHARED';
    userIds: number[];
    departmentIds: number[];
  };
};

const getQuestionGroup = (questionGroup: Nullable<QuestionGroup>) => {
  return questionGroup
    ? questionGroup
    : {
        name: '',
        description: '',
        questionIds: [],
        visibility: 'PRIVATE' as const,
        userIds: [],
        departmentIds: [],
      };
};

const getUserIds = (questionGroup: Nullable<QuestionGroup>) => {
  return questionGroup
    ? [questionGroup.createdBy, questionGroup.updatedBy]
    : [];
};

export const useQuestionGroupFormModal = ({
  questionGroup,
  onClose,
}: EditQuestionFormProps) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const isCreatingQuestionGroup = !questionGroup;
  const isShareQuestionGroupEnabled = useFlag(FlagType.ShareQuestionGroup);

  const handleClose = () => {
    setQuestionGroupDraft(getQuestionGroup(null));
    onClose?.();
  };

  useEffect(() => {
    setQuestionGroupDraft(getQuestionGroup(questionGroup));
  }, [questionGroup]);

  const [questionGroupDraft, setQuestionGroupDraft] =
    useState<QuestionGroupDraft>(getQuestionGroup(questionGroup));

  const [hasErrors, setHasErrors] = useState(false);
  const { users } = useResolveUsers({
    userIds: getUserIds(questionGroup),
  });

  const { toast } = useToast();

  const [updateQuestionGroup, { isLoading: isUpdating }] =
    api.endpoints.updateQuestionGroup.useMutation();

  const [createQuestionGroup, { isLoading: isCreating }] =
    api.endpoints.createQuestionGroup.useMutation();

  const questionInitialState = {
    name: { value: questionGroupDraft.name },
    description: { value: questionGroupDraft.description },
    questions: { value: questionGroupDraft.questionIds },
    shareField: {
      value: {
        visibility: questionGroupDraft.visibility,
        userIds: questionGroupDraft.userIds,
        departmentIds: questionGroupDraft.departmentIds,
      },
    },
  };

  const dispatch = useDispatch();

  const openSideContent = (tab?: SideContentTabs) => {
    dispatch(chatbot.actions.openSideContent(tab));
  };

  const handleOnSubmit = async () => {
    const operation = isCreatingQuestionGroup
      ? createQuestionGroup
      : updateQuestionGroup;
    try {
      await operation({
        id: questionGroup?.id ?? '',
        name: questionGroupDraft.name,
        description: questionGroupDraft.description,
        questionIds: questionGroupDraft.questionIds,
        visibility: questionGroupDraft.visibility,
        userIds: questionGroupDraft.userIds,
        departmentIds: questionGroupDraft.departmentIds,
      }).unwrap();
      toast({
        message: isCreatingQuestionGroup
          ? intl.formatMessage({
              id: 'UseQuestionGroupFormModal.createSuccessMessage',
              defaultMessage:
                'The question group has been successfully created.',
              description: 'Success message when question group is created',
            })
          : intl.formatMessage({
              id: 'UseQuestionGroupFormModal.updateSuccessMessage',
              defaultMessage:
                'The question group has been successfully updated.',
              description: 'Success message when question group is updated',
            }),
        status: 'success',
        action: {
          text: intl.formatMessage({
            id: 'UseQuestionGroupFormModal.viewAction',
            defaultMessage: 'View',
            description: 'Action button text to view question groups',
          }),
          level: 'action',
          onClick: () => {
            openSideContent('questionGroups');
          },
        },
      });
      isCreatingQuestionGroup && close();
    } catch (apiError: any) {
      const message = isCreatingQuestionGroup
        ? intl.formatMessage(
            {
              id: 'UseQuestionGroupFormModal.createErrorMessage',
              defaultMessage:
                'The question group creation failed due to {errorTitle}. {errorDetail}',
              description:
                'Error message when question group creation fails. Example: errorTitle = "Network Error", errorDetail = "Check connection"',
            },
            {
              errorTitle: apiError.errors[0].title,
              errorDetail: apiError.errors[0].detail,
            },
          )
        : intl.formatMessage(
            {
              id: 'UseQuestionGroupFormModal.updateErrorMessage',
              defaultMessage:
                'The question group update failed due to {errorTitle}. {errorDetail}',
              description:
                'Error message when question group update fails. Example: errorTitle = "Network Error", errorDetail = "Check connection"',
            },
            {
              errorTitle: apiError.errors[0].title,
              errorDetail: apiError.errors[0].detail,
            },
          );

      toast({
        status: 'danger',
        message,
      });
    }
  };

  const handleOnChange = ({
    state,
    errors,
  }: StateUpdater<QuestionGroupForm>) => {
    setHasErrors(Object.values(errors || {}).some((error) => error.length > 0));
    setQuestionGroupDraft({
      name: state.name.value,
      description: state.description.value,
      questionIds: state.questions.value,
      visibility: state.shareField.value.visibility,
      userIds: state.shareField.value.userIds,
      departmentIds: state.shareField.value.departmentIds,
    });
  };

  const handleOnValidate = ({ errors }: StateUpdater<QuestionGroupForm>) => {
    setHasErrors(Object.values(errors || {}).some((error) => error.length > 0));
  };

  const groupId = questionGroup?.id;

  const renderForm = () => {
    return (
      <Layout spacing={4} direction="column">
        <Text>
          {intl.formatMessage({
            id: 'UseQuestionGroupFormModal.formDescription',
            defaultMessage:
              'Input a name and description for the question group and select questions you would like added to the Question Group.',
            description: 'Description text for the question group form',
          })}
        </Text>
        <Form<QuestionGroupForm>
          fields={getFields(isShareQuestionGroupEnabled, groupId, intl)}
          onChange={handleOnChange}
          onValidate={handleOnValidate}
          state={questionInitialState}
          validators={[
            requiredValidator(['name', 'questions'], intl),
            ...(isShareQuestionGroupEnabled ? [validateShareField] : []),
          ]}
        />
        {questionGroup && (
          <Layout flex={1} my={4} spacing={4}>
            <Layout direction="column" flex={1}>
              <Text variant="tiny">
                {intl.formatMessage({
                  id: 'UseQuestionGroupFormModal.createdLabel',
                  defaultMessage: 'Created',
                  description: 'Label for creation information',
                })}
              </Text>
              <Layout spacing={1} direction="column">
                <Layout spacing={2}>
                  <Icon icon="field-date" size="s" />
                  <Text>{formatDate(questionGroup.createdAt, 'long')}</Text>
                </Layout>
                <Layout spacing={2}>
                  <Icon icon="user" size="s" />
                  <User user={users?.[questionGroup.createdBy]} mode="name" />
                </Layout>
              </Layout>
            </Layout>
            <Layout direction="column" flex={1}>
              <Text variant="tiny">
                {intl.formatMessage({
                  id: 'UseQuestionGroupFormModal.modifiedLabel',
                  defaultMessage: 'Modified',
                  description: 'Label for modification information',
                })}
              </Text>
              <Layout spacing={1} direction="column">
                <Layout spacing={2}>
                  <Icon icon="field-date" size="s" />
                  <Text>{formatDate(questionGroup.updatedAt, 'long')}</Text>
                </Layout>
                <Layout spacing={2}>
                  <Icon icon="user" size="s" />
                  <User user={users?.[questionGroup.createdBy]} mode="name" />
                </Layout>
              </Layout>
            </Layout>
          </Layout>
        )}
      </Layout>
    );
  };

  const [modal, open, close] = useModal({
    title: isCreatingQuestionGroup
      ? intl.formatMessage({
          id: 'UseQuestionGroupFormModal.createTitle',
          defaultMessage: 'Create a Question Group',
          description: 'Title for create question group modal',
        })
      : intl.formatMessage({
          id: 'UseQuestionGroupFormModal.editTitle',
          defaultMessage: 'Edit',
          description: 'Title for edit question group modal',
        }),
    children: renderForm(),
    onHide: handleClose,
    primaryAction: {
      text: isCreatingQuestionGroup
        ? intl.formatMessage({
            id: 'UseQuestionGroupFormModal.createAction',
            defaultMessage: 'Create',
            description: 'Create button text',
          })
        : intl.formatMessage({
            id: 'UseQuestionGroupFormModal.saveAction',
            defaultMessage: 'Save',
            description: 'Save button text',
          }),
      onClick: handleOnSubmit,
      isLoading: isCreating || isUpdating,
      disabled: hasErrors,
      tooltip: hasErrors
        ? intl.formatMessage({
            id: 'UseQuestionGroupFormModal.fillRequiredFieldsTooltip',
            defaultMessage: 'Please fill in all required fields',
            description: 'Tooltip when required fields are not filled',
          })
        : undefined,
    },
  });

  return [modal, open, close] as const;
};
